import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PostDeleteEquipmentFileInfo } from 'src/app/models/equipment-file/post-delete-equipment-file-info';
import { PostEquipmentFileInfo } from 'src/app/models/equipment-file/post-equipment-file-info';
import { ResponseUploadEquipmentFile } from 'src/app/models/equipment-file/response-upload-equipment-file';
import { ReturnEquipmentFilePayload } from 'src/app/models/equipment-file/return-equipment-file-payload';
import { ReturnEquipmentFilesPayload } from 'src/app/models/equipment-file/return-equipment-files-payload';
import { PostExistingEquipmentCreate } from 'src/app/models/equipment/post-existing-equipment-create';
import { PostGenericEquipmentUpdate } from 'src/app/models/equipment/post-generic-equipment-update';
import { PostModelNumberAutocomplete } from 'src/app/models/equipment/post-model-number-autocomplete';
import { PostRecommendationEquipmentCreate } from 'src/app/models/equipment/post-recommendation-equipment-create';
import { ResponseCreateRecommendationEquipment } from 'src/app/models/equipment/response-create-recommendation-equipment';
import { ReturnEquipmentAfterUpdate } from 'src/app/models/equipment/return-equipment-after-update';
import { ReturnEquipmentHeader } from 'src/app/models/equipment/return-equipment-header';
import { ReturnGenericEquipmentPayload } from 'src/app/models/equipment/return-generic-equipment-payload';
import { ReturnMessage } from 'src/app/models/return-message';
import { SearchResultEquipmentPayload } from 'src/app/models/search/search-result-equipment-payload';
import { PostFilterFields } from 'src/app/models/shared/post-filter-fields';

@Injectable({
  providedIn: 'root'
})
export class EquipmentService {

  private apis = {
    add: 'mfqAppEquipment/Create/',
    addRecommendationEquipment: 'mfqAppEquipment/CreateRecommendationEquipment/',
    update: 'mfqAppEquipment/Update/',
    list: 'mfqAppAddress/GetEquipment/',
    getEquipment: 'mfqAppEquipment/Get/',
    getHeaderDetails: 'mfqAppEquipment/GetHeader/',
    autocompleteEquipmentType: 'mfqAppEquipment/AutocompleteEquipmentType/',
    autocompleteManufacturer: 'mfqAppEquipment/AutocompleteManufacturer/',
    autocompleteModelNumber: 'mfqAppEquipment/AutocompleteModelNumber/',
    autocompleteInstallationBy: 'mfqAppEquipment/AutocompleteInstallationBy/',
    autocompleteExtendedWarrantyType: 'mfqAppEquipment/AutocompleteExtendedWarrantyType/',
    autocompleteServiceLevel: 'mfqAppEquipment/AutocompleteServiceLevel/',
    getStagingEquipmentFiles: 'mfqAppEquipment/GetStagingEquipmentFiles/',
    getEquipmentFiles: 'mfqAppEquipment/GetEquipmentFiles/',
    uploadEquipmentFile: 'mfqAppEquipment/UploadEquipmentFile/',
    deleteEquipmentFile: 'mfqAppEquipment/DeleteEquipmentFile/',
    clearStagingEquipmentFileTable: 'mfqAppEquipment/ClearStagingEquipmentFileTable/',
    getFullImage: 'mfqAppEquipment/GetFullImage/'
  };

  constructor(private http: HttpClient) { }

  autocompleteEquipmentType(data: PostFilterFields): Observable<ReturnMessage<any[]>> {
    return this.http.post<ReturnMessage<any[]>>(this.apis.autocompleteEquipmentType, data);
  }

  autocompleteManufacturer(data: PostFilterFields): Observable<ReturnMessage<any[]>> {
    return this.http.post<ReturnMessage<any[]>>(this.apis.autocompleteManufacturer, data);
  }

  autocompleteModelNumber(data: PostModelNumberAutocomplete): Observable<ReturnMessage<any[]>> {
    return this.http.post<ReturnMessage<any[]>>(this.apis.autocompleteModelNumber, data);
  }

  autocompleteInstallationBy(data: PostFilterFields): Observable<ReturnMessage<any[]>> {
    return this.http.post<ReturnMessage<any[]>>(this.apis.autocompleteInstallationBy, data);
  }

  autocompleteExtendedWarrantyType(data: PostFilterFields): Observable<ReturnMessage<any[]>> {
    return this.http.post<ReturnMessage<any[]>>(this.apis.autocompleteExtendedWarrantyType, data);
  }

  autocompleteServiceLevel(data: PostFilterFields): Observable<ReturnMessage<any[]>> {
    return this.http.post<ReturnMessage<any[]>>(this.apis.autocompleteServiceLevel, data);
  }

  list(addressId: string, addressType: string): Observable<ReturnMessage<SearchResultEquipmentPayload>> {
    const options = {
      params: new HttpParams()
        .set('id', addressId)
        .set('type', addressType)
    };
    return this.http.get<ReturnMessage<SearchResultEquipmentPayload>>(this.apis.list, options);
  }

  listFilter(addressId: string, addressType: string, filterData: PostFilterFields): Observable<ReturnMessage<SearchResultEquipmentPayload>> {
    const options = {
      params: new HttpParams()
        .set('id', addressId)
        .set('type', addressType)
    };
    return this.http.post<ReturnMessage<SearchResultEquipmentPayload>>(this.apis.list, filterData, options);
  }

  add(addressId: string, addressType: string, data: PostExistingEquipmentCreate): Observable<ReturnMessage<any>> {
    const options = {
      params: new HttpParams()
        .set('id', addressId)
        .set('type', addressType)
    };
    return this.http.post<ReturnMessage<any>>(this.apis.add, data, options);
  }

  addRecommendationEquipment(data: PostRecommendationEquipmentCreate): Observable<ReturnMessage<ResponseCreateRecommendationEquipment>> {
    return this.http.post<ReturnMessage<ResponseCreateRecommendationEquipment>>(this.apis.addRecommendationEquipment, data);
  }

  update(data: PostGenericEquipmentUpdate): Observable<ReturnMessage<ReturnEquipmentAfterUpdate>> {
    return this.http.post<ReturnMessage<ReturnEquipmentAfterUpdate>>(this.apis.update, data);
  }

  getHeaderDetails(equipmentId: string, equipmentType: string): Observable<ReturnMessage<ReturnEquipmentHeader>> {
    const options = {
      params: new HttpParams()
        .set('id', equipmentId)
        .set('type', equipmentType)
    };
    return this.http.get<ReturnMessage<ReturnEquipmentHeader>>(this.apis.getHeaderDetails, options);
  }

  get(equipmentId: string, equipmentType: string): Observable<ReturnMessage<ReturnGenericEquipmentPayload>> {
    const options = {
      params: new HttpParams()
        .set('id', equipmentId)
        .set('type', equipmentType)
    };
    return this.http.get<ReturnMessage<ReturnGenericEquipmentPayload>>(this.apis.getEquipment, options);
  }

  getStagingEquipmentFiles(): Observable<ReturnMessage<ReturnEquipmentFilesPayload>> {
    return this.http.post<ReturnMessage<ReturnEquipmentFilesPayload>>(this.apis.getStagingEquipmentFiles, null);
  }

  getEquipmentFiles(data: PostEquipmentFileInfo): Observable<ReturnMessage<ReturnEquipmentFilesPayload>> {
    return this.http.post<ReturnMessage<ReturnEquipmentFilesPayload>>(this.apis.getEquipmentFiles, data);
  }

  uploadEquipmentFile(data: FormData): Observable<ReturnMessage<ResponseUploadEquipmentFile>> {
    return this.http.post<ReturnMessage<ResponseUploadEquipmentFile>>(this.apis.uploadEquipmentFile, data);
  }

  deleteEquipmentFile(data: PostDeleteEquipmentFileInfo): Observable<ReturnMessage<any>> {
    return this.http.post<ReturnMessage<any>>(this.apis.deleteEquipmentFile, data);
  }

  clearStagingEquipmentFileTable(): Observable<ReturnMessage<any>> {
    return this.http.post<ReturnMessage<any>>(this.apis.clearStagingEquipmentFileTable, null);
  }

  getFullImage(id: string): Observable<ReturnMessage<ReturnEquipmentFilePayload>> {
    const options = {
      params: new HttpParams()
        .set('id', id)
    };
    return this.http.get<ReturnMessage<ReturnEquipmentFilePayload>>(this.apis.getFullImage, options);
  }
}
